import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faChevronDown,
	faTruckPickup,
	faTools,
	faTrash,
	faCheck,
	faTimes,
	faHandshake,
} from "@fortawesome/free-solid-svg-icons"
import moment from "moment"
import { useGroups } from "../../hooks/groups"
import { generalStyles as gs } from "../styles/styles"
import { tableStyles } from "../styles/tableStyles"
import { COLORS } from "../../utilities/Constants"
import { locationTable } from "../PDF/LocationTable"
import {
	deleteLocation,
	deleteLocationReset,
	setLocationSearchResults,
} from "../../store/actions/locationSearchActions"
import ResultDeleteModal from "../others/ResultDeleteModal"
import ResultNumCell, { RESULT_ICON_SIZE } from "../others/ResultNumCell"
import { fetchFichesOuvertes } from "../../store/actions/ficheOuverteActions"
import { detenteurFiche } from "../../hooks/useFicheVerrou"
import { setFeuilletage } from "../../store/actions/feuilletageActions"
import { getOpenerId } from "../../utilities/session"

// Résultats de recherche location (V2). Page 2/2, jumelle d'AtelierResultatsV2
// et de DepResultatsV2 : dossier vert « dernière fiche consultée », cadenas
// « verrouillée par une autre session », tri par colonne, feuilletage figé sur
// l'ordre affiché. Le tableau vient de LocationSearch (page unique jusqu'au
// 31.07.2026), le formulaire est resté là-bas.
const ts = (date, heure) =>
	moment(`${date} ${heure}`, "DD-MM-YYYY kk:mm").valueOf()

// Un comparateur par colonne. client/vehLoc départagent sur la date de début.
const COMPARATORS = {
	dateDebut: (a, b) =>
		ts(a.dateDebut, a.heureDebut) - ts(b.dateDebut, b.heureDebut),
	dateResti: (a, b) =>
		ts(a.dateResti, a.heureResti) - ts(b.dateResti, b.heureResti),
	client: (a, b) =>
		(a.client || "").localeCompare(b.client || "", "fr") ||
		ts(a.dateDebut, a.heureDebut) - ts(b.dateDebut, b.heureDebut),
	vehLoc: (a, b) =>
		String(a.vehLoc || "").localeCompare(String(b.vehLoc || ""), "fr") ||
		ts(a.dateDebut, a.heureDebut) - ts(b.dateDebut, b.heureDebut),
	// QA Jordan 27.10 : total de la fiche (location prévue + suppléments).
	total: (a, b) => Number(a.total_facture) - Number(b.total_facture),
	etat: (a, b) => (a.etat || 0) - (b.etat || 0),
}
// Mapping vers l'ordre serveur utilisé par la re-recherche après suppression.
const ORDER_BY = {
	dateDebut: "dateD",
	dateResti: "dateR",
	client: "client",
	vehLoc: "vehLoc",
	total: "total",
	etat: "etat",
}

const SortableTh = ({ label, colKey, sort, onSort, right }) => {
	const active = sort.key === colKey
	return (
		<th style={right ? tableStyles.thRight : tableStyles.th}>
			<span style={styles.thInner}>
				{label}
				<button
					type="button"
					style={styles.sortBtn(active, sort.asc)}
					onClick={() => onSort(colKey)}
				>
					<FontAwesomeIcon icon={faChevronDown} />
				</button>
			</span>
		</th>
	)
}

const ResultRow = ({
	fiche,
	index,
	isBoss,
	isOpen,
	lockedBy,
	onDoubleClick,
	onOpenDep,
	onOpenAtelier,
	onDelete,
}) => {
	const [hover, setHover] = useState(false)
	// Plus de fond de ligne porteur de sens (décision Jordan 31.07.2026) : le
	// prêt est une icône en 1re colonne, la ligne garde zébrage + hover.
	const bg = hover ? tableStyles.rowHover : tableStyles.rowBg(index)
	const stop = (fn) => (e) => {
		e.stopPropagation()
		fn()
	}
	return (
		<tr
			onDoubleClick={onDoubleClick}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				cursor: "pointer",
				backgroundColor: bg,
				transition: "background-color 0.12s",
			}}
		>
			<ResultNumCell index={index} isOpen={isOpen} lockedBy={lockedBy}>
				{/* Prêt : statut propre à la location. C'était un fond de ligne
				    bleu jusqu'au 31.07.2026 — il écrasait zébrage et hover. */}
				{fiche.isPret && (
					<FontAwesomeIcon
						icon={faHandshake}
						title="Prêt"
						style={{
							color: COLORS.accent,
							fontSize: RESULT_ICON_SIZE,
						}}
					/>
				)}
			</ResultNumCell>
			<td
				style={styles.td}
			>{`${fiche.dateDebut} à ${fiche.heureDebut}`}</td>
			<td
				style={styles.td}
			>{`${fiche.dateResti} à ${fiche.heureResti}`}</td>
			<td style={styles.td}>{fiche.client}</td>
			<td style={styles.td}>{fiche.vehLoc}</td>
			<td style={styles.td}>{fiche.etatTexte || ""}</td>
			<td style={{ ...styles.td, textAlign: "center" }}>
				<FontAwesomeIcon
					icon={fiche.restitue ? faCheck : faTimes}
					style={{
						color: fiche.restitue ? COLORS.success : COLORS.danger,
					}}
				/>
			</td>
			<td style={styles.td}>{fiche.total_facture}</td>
			<td style={styles.td}>
				<div style={styles.toolsCell}>
					{fiche.dep > 0 && (
						<button
							type="button"
							style={gs.iconBtn(COLORS.text)}
							title="Fiche dépannage"
							onClick={stop(onOpenDep)}
						>
							<FontAwesomeIcon icon={faTruckPickup} />
						</button>
					)}
					{fiche.atelier > 0 && (
						<button
							type="button"
							style={gs.iconBtn(COLORS.text)}
							title="Fiche atelier"
							onClick={stop(onOpenAtelier)}
						>
							<FontAwesomeIcon icon={faTools} />
						</button>
					)}
					{isBoss && (
						<button
							type="button"
							style={gs.iconBtn(COLORS.danger)}
							title="Supprimer"
							onClick={stop(onDelete)}
						>
							<FontAwesomeIcon icon={faTrash} />
						</button>
					)}
				</div>
			</td>
		</tr>
	)
}

const LocationResultatsV2 = () => {
	const dispatch = useDispatch()
	const history = useHistory()
	const { isBoss } = useGroups()
	const {
		locationFiches,
		criteres,
		deleteLocation: deleteState,
	} = useSelector((state) => state.locationSearchInfo)
	const openLocationId = useSelector(
		(state) => state.locationInfo.location.id,
	)
	const fichesOuvertes = useSelector(
		(state) => state.fichesOuvertesInfo.fichesOuvertes,
	)

	// Cadenas : nom du détenteur si la fiche est verrouillée par une AUTRE
	// session (même règle et même affichage que les résultats atelier/dép).
	const openerId = getOpenerId()
	const lockedBy = (ficheId) => {
		const detenteur = detenteurFiche(
			fichesOuvertes,
			"loc",
			ficheId,
			openerId,
		)
		return detenteur ? detenteur.user : null
	}

	useEffect(() => {
		dispatch(fetchFichesOuvertes())
	}, [dispatch])

	const [sort, setSort] = useState({ key: "dateDebut", asc: true })
	const [modalDelete, setModalDelete] = useState(null) // {id, title}

	// Copie triée — on ne mute jamais le tableau redux.
	const sortedFiches = useMemo(() => {
		const cmp = COMPARATORS[sort.key]
		const dir = sort.asc ? 1 : -1
		return cmp
			? [...locationFiches].sort((a, b) => dir * cmp(a, b))
			: [...locationFiches]
	}, [locationFiches, sort])

	const onSort = (key) =>
		setSort((prev) =>
			prev.key === key ? { key, asc: !prev.asc } : { key, asc: true },
		)

	const openFiche = (id) => {
		// Persiste l'ordre trié dans le slice de recherche (on le retrouve au
		// retour), puis fige la liste de feuilletage sur ce MÊME ordre : Page
		// Up/Down de la fiche suit exactement ce que montre le tableau.
		dispatch(setLocationSearchResults(sortedFiches))
		dispatch(
			setFeuilletage(
				"loc",
				sortedFiches.map((f) => f.id),
				"locationSearch",
			),
		)
		history.push(`/location/fiche/${id}`)
	}

	const doDelete = () => {
		// Le backend DELETE relance la recherche : mêmes critères que le GET
		// (mémorisés au clic « Rechercher ») + l'ordre affiché.
		dispatch(
			deleteLocation(
				JSON.stringify({
					id: modalDelete.id,
					// Sans critères V2 : pas de relance (un {} faisait planter la
					// recherche du DELETE, KeyError → 500 après la suppression).
					search: criteres || undefined,
					orderBy: ORDER_BY[sort.key] || "",
				}),
			),
		)
	}
	// Purge d'abord le slice : un état resté d'un flux interrompu (page quittée
	// en pleine suppression) rouvrirait le modal sur un ✓ ou une erreur périmés.
	const openDelete = (fiche) => {
		dispatch(deleteLocationReset())
		setModalDelete(fiche)
	}
	const closeDelete = () => {
		setModalDelete(null)
		dispatch(deleteLocationReset())
	}

	// Raccourci repris de LocationSearch : F11 → impression PDF du tableau.
	useEffect(() => {
		const onKey = (e) => {
			if (e.key !== "F11") return
			e.preventDefault()
			if (sortedFiches.length > 0) locationTable(sortedFiches)
		}
		document.addEventListener("keydown", onKey)
		return () => document.removeEventListener("keydown", onKey)
	}, [sortedFiches])

	return (
		<div style={gs.page}>
			<div style={styles.shell}>
				<div style={gs.header}>
					<div>
						<div style={gs.ficheNumber}>Location</div>
						<div style={gs.title}>
							Résultats ({locationFiches.length})
						</div>
					</div>
					<div style={styles.headerActions}>
						{sortedFiches.length > 0 && (
							<button
								type="button"
								style={gs.btnSecondary}
								onClick={() => locationTable(sortedFiches)}
							>
								Imprimer
							</button>
						)}
						<button
							type="button"
							style={gs.btnPrimary}
							onClick={() => history.push("/location/rechercher")}
						>
							Modifier la recherche
						</button>
					</div>
				</div>

				{sortedFiches.length > 0 ? (
					<div style={tableStyles.container}>
						<table style={tableStyles.table}>
							<thead>
								<tr>
									<th style={tableStyles.th}>N°</th>
									<SortableTh
										label="Date de début"
										colKey="dateDebut"
										sort={sort}
										onSort={onSort}
									/>
									<SortableTh
										label="Date de restitution"
										colKey="dateResti"
										sort={sort}
										onSort={onSort}
									/>
									<SortableTh
										label="Client"
										colKey="client"
										sort={sort}
										onSort={onSort}
									/>
									<SortableTh
										label="Véhicule de location"
										colKey="vehLoc"
										sort={sort}
										onSort={onSort}
									/>
									<SortableTh
										label="État"
										colKey="etat"
										sort={sort}
										onSort={onSort}
									/>
									<th style={tableStyles.th}>Restitué</th>
									<SortableTh
										label="Total"
										colKey="total"
										sort={sort}
										onSort={onSort}
									/>
									<th
										style={{
											...tableStyles.th,
											textAlign: "right",
										}}
									>
										Outils
									</th>
								</tr>
							</thead>
							<tbody>
								{sortedFiches.map((fiche, index) => (
									<ResultRow
										key={`ficheLocation${fiche.id}`}
										fiche={fiche}
										index={index}
										isBoss={isBoss}
										isOpen={
											openLocationId > 0 &&
											openLocationId === fiche.id
										}
										lockedBy={lockedBy(fiche.id)}
										onDoubleClick={() =>
											openFiche(fiche.id)
										}
										onOpenDep={() =>
											history.push(
												`/depannage/fiche/${fiche.dep}`,
												{ from: "locationSearch" },
											)
										}
										onOpenAtelier={() =>
											history.push(
												`/atelier/fiche/${fiche.atelier}`,
												{ from: "locationSearch" },
											)
										}
										onDelete={() =>
											openDelete({
												id: fiche.id,
												title: `Location du ${fiche.dateDebut}`,
											})
										}
									/>
								))}
							</tbody>
						</table>
					</div>
				) : (
					<div style={tableStyles.container}>
						<div style={styles.empty}>
							Aucune fiche de location trouvée
						</div>
					</div>
				)}
			</div>

			{modalDelete && (
				<ResultDeleteModal
					label={modalDelete.title}
					status={deleteState}
					onConfirm={doDelete}
					onClose={closeDelete}
				/>
			)}
		</div>
	)
}

const styles = {
	shell: {
		width: "100%",
		maxWidth: 1400,
		margin: "0 auto",
	},
	headerActions: {
		display: "flex",
		gap: 10,
		alignItems: "center",
	},
	thInner: {
		display: "inline-flex",
		alignItems: "center",
		gap: 7,
	},
	sortBtn: (active, asc) => ({
		border: "none",
		background: "transparent",
		padding: 2,
		cursor: "pointer",
		fontSize: 11,
		color: active ? COLORS.text : COLORS.textLight,
		opacity: active ? 1 : 0.4,
		transform: active && asc ? "rotate(180deg)" : "none",
		transition: "transform 0.15s, opacity 0.12s, color 0.12s",
		lineHeight: 1,
	}),
	td: {
		...tableStyles.td,
		fontVariantNumeric: "tabular-nums",
	},
	toolsCell: {
		display: "flex",
		gap: 6,
		justifyContent: "flex-end",
	},
	empty: {
		...tableStyles.empty,
		padding: "40px 16px",
		color: COLORS.textLight,
		fontSize: 14,
	},
}

export default LocationResultatsV2
